import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { ThemeProps } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/themeSchema';
import { champaignUrlDomain } from '@mutiny-pkg/config/src/domain';
import { type LoadedCustomComponentModule, registerHostRuntime } from './hostRuntime';

export interface CustomComponentArtifactEntry {
  /** Editor-only — drives the design-sidebar attributes editor. Omitted on published pages. */
  propsSchema?: Record<string, unknown> | null;
}

export interface CustomComponentFilesystem {
  /**
   * Keyed by sourceHash. Stores per-artifact metadata (currently just propsSchema for the editor
   * sidebar). The renderer doesn't gate on entries being present here — `resolveCompiledUrl` is
   * the source of truth for whether bytes can be fetched for a given hash.
   */
  artifacts: Record<string, CustomComponentArtifactEntry>;
  /**
   * Slug → current canonical sourceHash for this page. Used by the artifact loader to resolve
   * `import X from 'components/<slug>'` imports inside a compiled artifact body. The outer node
   * pin always wins for what THIS node renders; this map only matters for transitive imports.
   */
  slugToCurrentSourceHash: Record<string, string>;
}

export interface CustomComponentContextValue {
  filesystem: CustomComponentFilesystem | null;
  theme: ThemeProps;
  overrideVariables: Record<string, string> | undefined;
  isEditor: boolean;
  loadModuleByHash: (sourceHash: string) => Promise<LoadedCustomComponentModule>;
}

export const CustomComponentContext = createContext<CustomComponentContextValue | null>(null);

interface CustomComponentProviderProps {
  filesystem: CustomComponentFilesystem | null;
  theme: ThemeProps;
  overrideVariables?: Record<string, string>;
  isEditor?: boolean;
  /**
   * Returns the URL to fetch the compiled bundle for a given `sourceHash`. The provider keeps the
   * URL strategy out-of-band (proxy in editor + preview, static-relative in published archives)
   * so the caller picks the strategy without baking it into the manifest. Renderers always call
   * this — they never need to look up `filesystem.artifacts[hash].compiledUrl`.
   */
  resolveCompiledUrl: (sourceHash: string) => string;
  /**
   * Returns the absolute URL to fetch the runtime shim for an `import` specifier baked into a
   * compiled artifact (always under `/__mutiny/runtime/`). Editor canvas points at champaign;
   * published pages point at the company's same-origin static CDN so the published page has no
   * runtime dependency on champaign and avoids the cross-origin CORS preflight that would fail
   * for customer domains. Defaults to a champaign-relative resolver for callers that haven't
   * been updated yet.
   */
  resolveRuntimeUrl?: (specifier: string) => string;
  children: React.ReactNode;
}

/**
 * Resolve the absolute origin where champaign serves the runtime shim. Used by the default
 * `resolveRuntimeUrl` (editor canvas) so callers that haven't been updated still work. Published
 * pages must override `resolveRuntimeUrl` — fetching from champaign is cross-origin from a
 * customer domain and the global CORS allowlist excludes them.
 */
function champaignAbsoluteOrigin(): string {
  const domain = champaignUrlDomain();
  const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
  return domain.startsWith('//') ? `${protocol}${domain}` : domain;
}

const RUNTIME_PATH_PREFIX = '/__mutiny/runtime/';

const defaultResolveRuntimeUrl = (specifier: string): string => `${champaignAbsoluteOrigin()}${specifier}`;

/**
 * Wrap any subtree that may render `custom-component` nodes. This provider:
 *   1. Registers the host React singleton + a `loadComponent(slug)` dispatcher on
 *      `globalThis.__mutinyHostRuntime` — backing the runtime shim module at
 *      `/__mutiny/runtime/v1/react.js`.
 *   2. Owns a sourceHash-keyed module cache + blob-URL cache so each artifact loads once across
 *      the page even if multiple slugs reference it.
 *   3. Exposes the active filesystem manifest + theme + override context to descendants.
 *
 * Mount the provider as high in the tree as possible — child `CustomComponent` nodes assume it's
 * present. If omitted, those nodes throw with a clear message during render.
 */
export function CustomComponentProvider({
  filesystem,
  theme,
  overrideVariables,
  isEditor = false,
  resolveCompiledUrl,
  resolveRuntimeUrl = defaultResolveRuntimeUrl,
  children,
}: CustomComponentProviderProps): React.ReactElement {
  const moduleCacheRef = useRef<Map<string, Promise<LoadedCustomComponentModule>>>();
  if (!moduleCacheRef.current) moduleCacheRef.current = new Map();
  const blobUrlCacheRef = useRef<Map<string, Promise<string>>>();
  if (!blobUrlCacheRef.current) blobUrlCacheRef.current = new Map();
  const inFlightRef = useRef<Set<string>>();
  if (!inFlightRef.current) inFlightRef.current = new Set();

  const filesystemRef = useRef(filesystem);
  filesystemRef.current = filesystem;
  const resolveCompiledUrlRef = useRef(resolveCompiledUrl);
  resolveCompiledUrlRef.current = resolveCompiledUrl;
  const resolveRuntimeUrlRef = useRef(resolveRuntimeUrl);
  resolveRuntimeUrlRef.current = resolveRuntimeUrl;

  const loadModuleByHash = useCallback(async (sourceHash: string): Promise<LoadedCustomComponentModule> => {
    return loadFromCache(
      moduleCacheRef.current!,
      blobUrlCacheRef.current!,
      inFlightRef.current!,
      sourceHash,
      resolveCompiledUrlRef.current,
      resolveRuntimeUrlRef.current,
      filesystemRef.current,
    );
  }, []);

  // The runtime's `loadComponent(slug)` is the entry point compiled artifacts call when they have
  // a `components/<slug>` bare import. We resolve slug → currentSourceHash via the page's canonical
  // map so transitive imports always pick up the latest published canonical version, not whatever
  // the outer node happens to be pinned to.
  const loadComponentBySlug = useCallback(
    async (slug: string): Promise<LoadedCustomComponentModule> => {
      const fs = filesystemRef.current;
      const sourceHash = fs?.slugToCurrentSourceHash[slug];
      if (!sourceHash) {
        throw new Error(`Custom component slug "${slug}" is not registered for this page.`);
      }
      return loadModuleByHash(sourceHash);
    },
    [loadModuleByHash],
  );

  // Register host runtime as early as possible — before children render. Doing it in useEffect is
  // too late: children may trigger a dynamic import during their first render, and the artifact's
  // shim would throw. useMemo runs during render, before descendants commit.
  useMemo(() => {
    registerHostRuntime({ React, loadComponent: loadComponentBySlug });
  }, [loadComponentBySlug]);

  useEffect(() => {
    registerHostRuntime({ React, loadComponent: loadComponentBySlug });
  }, [loadComponentBySlug]);

  const value = useMemo<CustomComponentContextValue>(
    () => ({
      filesystem,
      theme,
      overrideVariables,
      isEditor,
      loadModuleByHash,
    }),
    [filesystem, theme, overrideVariables, isEditor, loadModuleByHash],
  );

  return <CustomComponentContext.Provider value={value}>{children}</CustomComponentContext.Provider>;
}

async function loadFromCache(
  moduleCache: Map<string, Promise<LoadedCustomComponentModule>>,
  blobUrlCache: Map<string, Promise<string>>,
  inFlight: Set<string>,
  sourceHash: string,
  resolveCompiledUrl: (sourceHash: string) => string,
  resolveRuntimeUrl: (specifier: string) => string,
  filesystem: CustomComponentFilesystem | null,
): Promise<LoadedCustomComponentModule> {
  const cached = moduleCache.get(sourceHash);
  if (cached) return cached;
  const promise = (async () => {
    const blobUrl = await getBlobUrl(
      blobUrlCache,
      inFlight,
      sourceHash,
      resolveCompiledUrl,
      resolveRuntimeUrl,
      filesystem,
    );
    return import(/* @vite-ignore */ /* webpackIgnore: true */ blobUrl) as Promise<LoadedCustomComponentModule>;
  })();
  moduleCache.set(sourceHash, promise);
  promise.catch(() => {
    if (moduleCache.get(sourceHash) === promise) moduleCache.delete(sourceHash);
  });
  return promise;
}

async function getBlobUrl(
  blobUrlCache: Map<string, Promise<string>>,
  inFlight: Set<string>,
  sourceHash: string,
  resolveCompiledUrl: (sourceHash: string) => string,
  resolveRuntimeUrl: (specifier: string) => string,
  filesystem: CustomComponentFilesystem | null,
): Promise<string> {
  const cached = blobUrlCache.get(sourceHash);
  if (cached) return cached;
  if (inFlight.has(sourceHash)) {
    throw new Error(`Circular custom-component import detected at sourceHash ${sourceHash}.`);
  }
  inFlight.add(sourceHash);
  const promise = (async () => {
    const text = await fetchArtifactText(resolveCompiledUrl(sourceHash));
    const rewritten = await rewriteBareSpecifiers(
      text,
      resolveCompiledUrl,
      resolveRuntimeUrl,
      filesystem,
      blobUrlCache,
      inFlight,
    );
    return URL.createObjectURL(new Blob([rewritten], { type: 'text/javascript' }));
  })();
  blobUrlCache.set(sourceHash, promise);
  promise
    .catch(() => {
      if (blobUrlCache.get(sourceHash) === promise) blobUrlCache.delete(sourceHash);
    })
    .finally(() => {
      inFlight.delete(sourceHash);
    });
  return promise;
}

async function fetchArtifactText(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch custom-component artifact at ${url}: HTTP ${res.status}`);
  }
  return res.text();
}

const COMPONENT_IMPORT_PATTERN = /^components\/([a-z0-9][a-z0-9-]*)$/;

/**
 * Rewrite bare-specifier imports in an esbuild-emitted ESM artifact. Esbuild emits import
 * statements in a small uniform shape (`from "spec";` or `from 'spec';`, no comments inside the
 * statement, ASCII-only specifiers) so a focused regex over the leading import block is robust
 * enough — we don't need to vendor an ESM parser.
 */
async function rewriteBareSpecifiers(
  text: string,
  resolveCompiledUrl: (sourceHash: string) => string,
  resolveRuntimeUrl: (specifier: string) => string,
  filesystem: CustomComponentFilesystem | null,
  blobUrlCache: Map<string, Promise<string>>,
  inFlight: Set<string>,
): Promise<string> {
  const regex = /(\bfrom\s*['"])([^'"\n]+)(['"])/g;
  const candidates: Array<{ matchText: string; spec: string }> = [];
  for (const m of text.matchAll(regex)) {
    const matchText = m[0];
    const spec = m[2];
    if (matchText && spec) candidates.push({ matchText, spec });
  }
  // Resolve in parallel — each resolution either hits the per-sourceHash cache or recursively
  // builds a child blob URL. Since the cache promise is set before recursion, concurrent calls
  // for the same slug share one resolution.
  const resolutions = await Promise.all(
    candidates.map(async ({ matchText, spec }) => {
      const replacement = await resolveSpecifier(
        spec,
        resolveCompiledUrl,
        resolveRuntimeUrl,
        filesystem,
        blobUrlCache,
        inFlight,
      );
      return replacement && replacement !== spec ? { matchText, spec, replacement } : null;
    }),
  );
  // Apply replacements after resolution so we don't re-match rewritten URLs.
  return resolutions.reduce<string>((acc, r) => {
    if (!r) return acc;
    return acc.replace(r.matchText, r.matchText.replace(r.spec, r.replacement));
  }, text);
}

async function resolveSpecifier(
  spec: string,
  resolveCompiledUrl: (sourceHash: string) => string,
  resolveRuntimeUrl: (specifier: string) => string,
  filesystem: CustomComponentFilesystem | null,
  blobUrlCache: Map<string, Promise<string>>,
  inFlight: Set<string>,
): Promise<string | null> {
  // The compiler bakes `/__mutiny/runtime/react/<version>.js` into newly-compiled artifacts.
  // Hand the spec to the caller-supplied resolver — editor canvas points at champaign, published
  // pages point at their same-origin static CDN (avoids the cross-origin CORS preflight that
  // would fail for customer domains).
  if (spec.startsWith(RUNTIME_PATH_PREFIX)) {
    return resolveRuntimeUrl(spec);
  }
  const componentMatch = COMPONENT_IMPORT_PATTERN.exec(spec);
  if (componentMatch) {
    const slug = componentMatch[1];
    if (!slug) return null;
    const sourceHash = filesystem?.slugToCurrentSourceHash[slug];
    if (!sourceHash) {
      throw new Error(`Custom component "${slug}" referenced by an artifact is not registered for this page.`);
    }
    return getBlobUrl(blobUrlCache, inFlight, sourceHash, resolveCompiledUrl, resolveRuntimeUrl, filesystem);
  }
  return null;
}

export function useCustomComponentContext(): CustomComponentContextValue | null {
  return useContext(CustomComponentContext);
}
